// ============================================================================
// PRODUCT IMAGES TAB COMPONENT
// Display product image gallery in WorkItemDetailModal
// Date: December 2025
// ============================================================================

'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Image as ImageIcon, Star, Trash2, Upload, ZoomIn, 
  ChevronLeft, ChevronRight, Loader2, X, GripVertical,
  Download, ExternalLink, Camera
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { useTheme } from '@/contexts/ThemeContext';
import { uploadFile, type UploadResult } from '@/lib/fileUpload';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';
import { confirm } from '@/lib/notifications';

// ============================================================================
// TYPES
// ============================================================================

interface ProductImage {
  id: number;
  work_item_id: string;
  file_name: string;
  file_path: string;
  file_size?: number;
  file_type?: string;
  mime_type?: string;
  is_primary: boolean;
  sort_order: number;
  alt_text?: string;
  caption?: string;
  thumbnail_url?: string;
  medium_url?: string;
  large_url?: string;
  uploaded_by_name?: string;
  created_at?: string;
}

interface ProductImagesTabProps {
  workItemId: string;
  isActive: boolean;
  readOnly?: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getAuthToken = (): string | null => {
  return authService.getTokens()?.access_token || null;
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getCloudStorageUploadUrl = (uploadResult: UploadResult): string => {
  const candidate = uploadResult.directUrl || uploadResult.publicUrl || uploadResult.url || '';

  if (!candidate) {
    throw new Error('Cloud storage upload did not return a usable file URL');
  }

  if (/staticfileserver/i.test(candidate)) {
    throw new Error('Upload returned a legacy static file server URL instead of cloud storage');
  }

  return candidate;
};

// ============================================================================
// LIGHTBOX COMPONENT
// ============================================================================

interface LightboxProps {
  images: ProductImage[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

function ImageLightbox({ images, currentIndex, onClose, onNavigate }: LightboxProps) {
  const currentImage = images[currentIndex];

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && currentIndex > 0) onNavigate(currentIndex - 1);
      if (e.key === 'ArrowRight' && currentIndex < images.length - 1) onNavigate(currentIndex + 1);
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, images.length, onClose, onNavigate]);

  if (!currentImage) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
      >
        <X className="w-6 h-6" />
      </button>

      <div className="absolute top-4 left-4 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
        {currentIndex + 1} / {images.length}
      </div>

      {currentIndex > 0 && (
        <button
          onClick={() => onNavigate(currentIndex - 1)}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      )}
      
      {currentIndex < images.length - 1 && (
        <button
          onClick={() => onNavigate(currentIndex + 1)}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      )}

      <div className="max-w-[90vw] max-h-[85vh] relative">
        <img
          src={currentImage.large_url || currentImage.file_path}
          alt={currentImage.alt_text || currentImage.file_name}
          className="max-w-full max-h-[85vh] object-contain rounded-lg"
        />
        
        {(currentImage.caption || currentImage.file_name) && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-4 rounded-b-lg">
            {currentImage.caption && <p className="text-center">{currentImage.caption}</p>}
            <p className="text-center text-sm text-gray-400 mt-1">{currentImage.file_name}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ProductImagesTab({ workItemId, isActive, readOnly = false }: ProductImagesTabProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  
  const [images, setImages] = useState<ProductImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [selectedImages, setSelectedImages] = useState<Set<number>>(new Set());
  const hasLoadedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadImages = useCallback(async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await fetch(`/api/v1/work-items/${workItemId}/product-images`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setImages(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error loading product images:', error);
    } finally {
      setLoading(false);
    }
  }, [workItemId]);

  useEffect(() => {
    hasLoadedRef.current = false;
  }, [workItemId]);

  // Load images when tab becomes active
  useEffect(() => {
    if (isActive && !hasLoadedRef.current) {
      loadImages();
      hasLoadedRef.current = true;
    }
  }, [isActive, loadImages]);

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || readOnly) return;

    setUploading(true);
    const token = getAuthToken();

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        // Upload to the cloud-storage company drive proxy.
        const uploadResult = await uploadFile(file, `product-images/${workItemId}`);
        const uploadedUrl = getCloudStorageUploadUrl(uploadResult);
        
        if (!uploadResult.success) {
          throw new Error(uploadResult.error || 'Upload failed');
        }

        // Save to database
        const response = await fetch(`/api/v1/work-items/${workItemId}/product-images`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            file_name: uploadResult.filename || file.name,
            file_path: uploadedUrl,
            file_size: file.size,
            file_type: file.name.split('.').pop()?.toLowerCase() || 'jpg',
            mime_type: file.type,
            is_primary: images.length === 0,
            sort_order: images.length,
            metadata: {
              storage_provider: 'cloud-storage',
              storage_token: uploadResult.storageToken || null,
              file_id: uploadResult.fileId || null,
              folder: `product-images/${workItemId}`,
            },
          })
        });

        if (!response.ok) {
          throw new Error('Failed to save image');
        }

      } catch (error) {
        console.error('Error uploading image:', error);
      }
    }

    setUploading(false);
    loadImages(); // Reload images
  };

  const handleSetPrimary = async (imageId: number) => {
    if (readOnly) return;
    
    try {
      const token = getAuthToken();
      const response = await fetch(`/api/v1/work-items/${workItemId}/product-images/${imageId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_primary: true })
      });

      if (response.ok) {
        setImages(prev => prev.map(img => ({
          ...img,
          is_primary: img.id === imageId
        })));
      }
    } catch (error) {
      console.error('Error setting primary image:', error);
    }
  };

  const handleDeleteImage = async (imageId: number) => {
    if (readOnly) return;
    
    if (!(await confirm({ message: 'Are you sure you want to delete this image?', variant: 'danger' }))) return;
    
    try {
      const token = getAuthToken();
      const response = await fetch(`/api/v1/work-items/${workItemId}/product-images/${imageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setImages(prev => prev.filter(img => img.id !== imageId));
        setSelectedImages(prev => {
          const newSet = new Set(prev);
          newSet.delete(imageId);
          return newSet;
        });
      }
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (readOnly || selectedImages.size === 0) return;
    
    if (!(await confirm({ message: `Are you sure you want to delete ${selectedImages.size} image(s)?`, variant: 'danger' }))) return;
    
    const token = getAuthToken();
    
    for (const imageId of selectedImages) {
      try {
        await fetch(`/api/v1/work-items/${workItemId}/product-images/${imageId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error deleting image:', error);
      }
    }

    loadImages();
    setSelectedImages(new Set());
  };

  const toggleImageSelection = (imageId: number) => {
    setSelectedImages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
        <span className={`ml-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading images...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Product Images
          </h3>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {images.length} image{images.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {selectedImages.size > 0 && !readOnly && (
            <button
              onClick={handleBulkDelete}
              className="px-3 py-1.5 text-sm rounded-lg bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" />
              Delete ({selectedImages.size})
            </button>
          )}

          {!readOnly && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                onChange={(e) => handleFileUpload(e.target.files)}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className={`px-4 py-2 rounded-lg text-white flex items-center gap-2 ${
                  uploading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload Images
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Image Grid */}
      {images.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {images.map((image, index) => (
            <div
              key={image.id}
              className={`relative group rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                image.is_primary
                  ? isDark
                    ? 'border-yellow-500 bg-yellow-900/10'
                    : 'border-yellow-400 bg-yellow-50'
                  : selectedImages.has(image.id)
                    ? isDark
                      ? 'border-blue-500 bg-blue-900/10'
                      : 'border-blue-400 bg-blue-50'
                    : isDark
                      ? 'border-gray-700 bg-gray-800'
                      : 'border-gray-200 bg-white'
              }`}
            >
              {/* Primary badge */}
              {image.is_primary && (
                <div className={`absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1 ${
                  isDark ? 'bg-yellow-500 text-black' : 'bg-yellow-400 text-yellow-900'
                }`}>
                  <Star className="w-3 h-3" fill="currentColor" />
                  Primary
                </div>
              )}

              {/* Selection checkbox */}
              {!readOnly && (
                <div 
                  className="absolute top-2 right-2 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleImageSelection(image.id);
                  }}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    selectedImages.has(image.id)
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : isDark
                        ? 'border-gray-500 bg-gray-700/50'
                        : 'border-gray-300 bg-white/80'
                  }`}>
                    {selectedImages.has(image.id) && (
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>
              )}

              {/* Image */}
              <div 
                className="aspect-square"
                onClick={() => setLightboxIndex(index)}
              >
                <img
                  src={image.thumbnail_url || image.file_path}
                  alt={image.alt_text || image.file_name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxIndex(index);
                    }}
                    className="p-2 rounded-full bg-white/90 text-gray-800 hover:bg-white"
                    title="View full size"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>

                  {!image.is_primary && !readOnly && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSetPrimary(image.id);
                      }}
                      className="p-2 rounded-full bg-yellow-400 text-yellow-900 hover:bg-yellow-300"
                      title="Set as primary"
                    >
                      <Star className="w-4 h-4" />
                    </button>
                  )}

                  {!readOnly && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteImage(image.id);
                      }}
                      className="p-2 rounded-full bg-red-500 text-white hover:bg-red-600"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Image info */}
              <div className={`p-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <p className="truncate font-medium">{image.file_name}</p>
                {image.file_size && (
                  <p>{formatFileSize(image.file_size)}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Empty state */
        <div className={`text-center py-12 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <Camera className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
          <h4 className={`text-lg font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            No Product Images
          </h4>
          <p className={`text-sm mb-4 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            Upload images to showcase this product
          </p>
          {!readOnly && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Upload First Image
            </button>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <ImageLightbox
          images={images}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </div>
  );
}
